import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/shared/hooks/use-toast';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { StudentAutocomplete, type Student } from '@/components/ui/StudentAutocomplete';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { StudentDisplay } from '@/components/ui/StudentDisplay';
import { CheckCircle2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { useI18n } from '@/contexts/I18nContext';
import { useQueryClient } from '@tanstack/react-query';
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig';
import { createUTCDateTime, formatDateTimeForDisplay } from '@/utils/timezoneUtils';
import { CLASS_CATEGORIES, getTrainingCategoryTranslationKey } from '@/constants/trainingCategories';
import { RecurrenceSelector } from '@/features/calendar/components/RecurrenceSelector';
import { RecurrenceConfig, generateRecurringDates, validateRecurrenceConfig, parseEndDate } from '@/utils/recurrenceUtils';
import { DateInput } from '@/components/ui/DateInput';
import { TimeInput } from '@/components/ui/TimeInput';
import { convertTimeTo24Hour } from '@/utils/dateTimeInputUtils';


// Helper function to validate if class time is within agenda configuration
const validateClassTime = (date: string, startTime: string, duration: number, agendaConfig: any): { isValid: boolean; message?: string } => {
  if (!agendaConfig || !agendaConfig.working_days || !agendaConfig.working_hours) {
    return { isValid: true }; // If no config, allow any time
  }

  // Get day of week
  const classDate = new Date(date);
  const dayOfWeek = classDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
  
  // Check if it's a working day
  if (!agendaConfig.working_days.includes(dayOfWeek)) {
    return { 
      isValid: false, 
      message: `Classes cannot be scheduled on ${dayOfWeek}. Please choose a working day.` 
    };
  }

  // Get working hours for this day
  const workingHours = agendaConfig.working_hours[dayOfWeek];
  if (!workingHours) {
    return { isValid: true }; // If no hours set for this day, allow any time
  }

  // Convert times to 24-hour format for comparison using central utility
  const startDateTimeISO = createUTCDateTime(date, startTime);
  const startDateTime = new Date(startDateTimeISO);
  const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
  
  // Convert working hours to local time for comparison
  const [openHour, openMinute] = workingHours.start.split(':').map(Number);
  const [closeHour, closeMinute] = workingHours.end.split(':').map(Number);
  
  // Create working hours in local time
  const workingDate = new Date(date + 'T00:00:00');
  const openDateTime = new Date(workingDate);
  openDateTime.setHours(openHour, openMinute, 0, 0);
  const closeDateTime = new Date(workingDate);
  closeDateTime.setHours(closeHour, closeMinute, 0, 0);

  // Convert class times to local time for comparison
  const classStartLocal = new Date(startDateTime.getTime() - (startDateTime.getTimezoneOffset() * 60000));
  const classEndLocal = new Date(endDateTime.getTime() - (endDateTime.getTimezoneOffset() * 60000));

  // Convert to minutes for easier comparison (all in local time)
  const classStartMinutes = classStartLocal.getHours() * 60 + classStartLocal.getMinutes();
  const classEndMinutes = classEndLocal.getHours() * 60 + classEndLocal.getMinutes();
  const openMinutes = openDateTime.getHours() * 60 + openDateTime.getMinutes();
  const closeMinutes = closeDateTime.getHours() * 60 + closeDateTime.getMinutes();

  // Check if class is within working hours (warning only, not blocking)
  const isOutsideWorkingHours = classStartMinutes < openMinutes || classEndMinutes > closeMinutes;
  
  if (isOutsideWorkingHours) {
    const openTimeFormatted = `${openHour.toString().padStart(2, '0')}:${openMinute.toString().padStart(2, '0')}`;
    const closeTimeFormatted = `${closeHour.toString().padStart(2, '0')}:${closeMinute.toString().padStart(2, '0')}`;
    
    return { 
      isValid: true, // Allow creation but with warning
      message: `⚠️ Class time is outside your working hours (${openTimeFormatted} - ${closeTimeFormatted}). This class will not affect your available slots.` 
    };
  }

  return { isValid: true };
};

// Helper function to get location name
const getLocationName = (location: string | { id: string; name: string }): string => {
  if (typeof location === 'string') return location;
  if (location && typeof location === 'object') return location.name;
  return '';
};

interface AddClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClassCreated?: (classData: any) => void;
  // Edit mode props
  mode?: 'create' | 'edit';
  classToEdit?: any;
  onClassUpdated?: () => void;
}

export const AddClassModal: React.FC<AddClassModalProps> = ({
  isOpen,
  onClose,
  onClassCreated,
  mode = 'create',
  classToEdit,
  onClassUpdated
}) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { config: agendaConfig } = useAgendaConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [timeWarning, setTimeWarning] = useState<string>('');

  const [classType, setClassType] = useState<string>(t('calendar.group_class'));
  const [modality, setModality] = useState<'Gi' | 'No-Gi'>('Gi');
  const [category, setCategory] = useState<string>('');
  const [location, setLocation] = useState<string | { id: string; name: string }>('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [description, setDescription] = useState('');
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | string | null>(null);
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [createdClass, setCreatedClass] = useState<any>(null);
  
  // Recurrence state - Default to "Does not repeat" (none)
  const [recurrenceConfig, setRecurrenceConfig] = useState<RecurrenceConfig>({
    enabled: false,
    frequency: 'none',
  });

  // Clear form and load user's academy when modal opens in create mode
  useEffect(() => {
    if (isOpen && mode === 'create') {
      // Reset form for new class
      setClassType('Group Class');
      setCategory('');
      setModality('Gi');
      setLocation('');
      setDate('');
      setStartTime('');
      setDuration(60);
      setDescription('');
      setStudents([]);
      setSelectedStudent(null);
      setStep('form');
      // Reset recurrence config to default (none)
      setRecurrenceConfig({
        enabled: false,
        frequency: 'none',
      });

      // Load user's main academy after form reset
      const loadUserMainAcademy = async () => {
        if (user?.id) {
          try {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('main_academy_id')
              .eq('id', user.id)
              .single();
            
            if (profileData?.main_academy_id) {
              const { data: academyData } = await supabase
                .from('academies')
                .select('id, name')
                .eq('id', profileData.main_academy_id)
                .single();
              
              if (academyData) {
                setLocation({ id: academyData.id, name: academyData.name });
              }
            }
          } catch (error) {
            console.error('Error loading user academy:', error);
          }
        }
      };

      loadUserMainAcademy();
    }
  }, [isOpen, mode, user?.id]);

  // Load class data when in edit mode
  React.useEffect(() => {
    if (mode === 'edit' && classToEdit) {
      setClassType(classToEdit.type || 'Group Class');
      setCategory(classToEdit.category || '');
      setModality(classToEdit.modality || 'Gi');
      setLocation(classToEdit.academy_name || '');
      
      // Use central utility to format datetime for display
      const { date: displayDate, time: displayTime } = formatDateTimeForDisplay(classToEdit.start_time);
      setDate(displayDate);
      setStartTime(displayTime);
      
      setDuration(Math.round((new Date(classToEdit.end_time).getTime() - new Date(classToEdit.start_time).getTime()) / 60000));
      setDescription(classToEdit.description || '');
      
      // Load students if it's a Private Class
      if (classToEdit.type === 'Private Class' && classToEdit.training_partners) {
        setStudents(classToEdit.training_partners.map((partner: any) => ({
          id: partner.partner_user_id,
          full_name: partner.profiles?.full_name || 'Unknown Student',
          username: partner.profiles?.username || 'unknown',
          profile_picture_url: partner.profiles?.profile_picture_url,
          belt_rank: partner.profiles?.belt_rank
        })));
      }
    }
  }, [mode, classToEdit]);


  // Validate time in real-time when date, time, or duration changes
  useEffect(() => {
    if (date && startTime && duration && agendaConfig) {
      // Only validate if time format looks complete (has AM/PM)
      // This prevents errors during typing/backspace
      const hasPeriod = /AM|PM/i.test(startTime);
      if (hasPeriod) {
        try {
          const validation = validateClassTime(date, startTime, duration, agendaConfig);
          if (!validation.isValid) {
            setTimeWarning(validation.message || '');
          } else {
            setTimeWarning('');
          }
        } catch (error) {
          // Silently ignore validation errors during typing
          setTimeWarning('');
        }
      } else {
        setTimeWarning('');
      }
    } else {
      setTimeWarning('');
    }
  }, [date, startTime, duration, agendaConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      toast({
        title: t('notifications.toasts.calendar_login_required'),
        description: t('notifications.toasts.calendar_login_required_description'),
        variant: "destructive",
      });
      return;
    }

    // If recurrence is enabled, time and endDate are required (date comes from recurrence)
    if (recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none') {
      if (!classType || !modality || !startTime || !recurrenceConfig.endDate) {
        toast({
          title: t('notifications.toasts.calendar_fields_required'),
          description: t('notifications.toasts.calendar_fields_required_description'),
          variant: "destructive",
        });
        return;
      }
    } else {
      // Otherwise, both date and time are required
      if (!classType || !modality || !date || !startTime) {
        toast({
          title: t('notifications.toasts.calendar_fields_required'),
          description: t('notifications.toasts.calendar_fields_required_description'),
          variant: "destructive",
        });
        return;
      }
    }

    setIsLoading(true);


    setIsLoading(true);

    try {
      // Validate class time against agenda configuration (only if not using recurrence or if date is provided)
      const dateToValidate = recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none' 
        ? new Date().toISOString().split('T')[0] 
        : date;
      
      if (dateToValidate) {
        const timeValidation = validateClassTime(dateToValidate, startTime, duration, agendaConfig);
        if (!timeValidation.isValid) {
          toast({
            title: t('notifications.toasts.calendar_invalid_schedule'),
            description: timeValidation.message,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
      }
      // In edit mode, if form.time is empty, use the original class time
      let timeToUse = startTime;
      
      // If recurrence is enabled, we don't need a specific date (will use today or first occurrence)
      let dateToUse = date;
      if (recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none') {
        // For recurrence, use today's date as the base (the actual dates come from generateRecurringDates)
        dateToUse = new Date().toISOString().split('T')[0];
      }
      
      if (!dateToUse) {
        toast({
          title: t('notifications.toasts.calendar_fields_required'),
          description: 'Date is required',
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const time24Hour = convertTimeTo24Hour(timeToUse);
      const [hours, minutes] = time24Hour.split(':').map(Number);
      const [year, month, day] = dateToUse.split('-').map(Number);
      
      // Create date in local timezone to preserve user's chosen time
      // For recurrence, this is just the base date - actual dates come from generateRecurringDates
      const baseStartTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const baseEndTime = new Date(baseStartTime);
      baseEndTime.setMinutes(baseEndTime.getMinutes() + duration);
      
      // Use local time directly - Supabase will handle timezone conversion
      const utcStartTime = baseStartTime;
      const utcEndTime = baseEndTime;
      
      let classData;
      
      if (mode === 'edit' && classToEdit) {
        // Update existing class
        const { data, error: classError } = await supabase
          .from('trainings')
          .update({
            title: classType,
            type: classType,
            category: category || null,
            modality,
            academy_name: getLocationName(location) || null,
            start_time: utcStartTime.toISOString(),
            end_time: utcEndTime.toISOString(),
            is_class: true
          })
          .eq('id', classToEdit.id)
          .select()
          .single();

        if (classError) {
          throw classError;
        }
        
        classData = data;
        
        // Update students for Private Class
        if (classType === 'Private Class') {
          // Remove existing students
          await supabase
            .from('training_partners')
            .delete()
            .eq('training_id', classToEdit.id);
          
          // Add new students
          if (students.length > 0) {
            const studentInserts = students
              .filter(student => student.username !== 'manual')
              .map(student => ({
                training_id: classToEdit.id,
                partner_user_id: student.id
              }));

            if (studentInserts.length > 0) {
              const { error: studentsError } = await supabase
                .from('training_partners')
                .insert(studentInserts);

              if (studentsError) {
                console.error('Error updating students:', studentsError);
              }
            }
          }
        } else {
          // Remove all students for Group Class
          await supabase
            .from('training_partners')
            .delete()
            .eq('training_id', classToEdit.id);
        }
        
        onClassUpdated?.();
      } else {
        // Create new class(es) - with or without recurrence
        // For classes, instructor_id should be the user creating the class (they are the instructor)
        const baseClassData = {
          user_id: user.id,
          instructor_id: user.id, // The user creating the class is the instructor
          title: classType,
          type: classType,
          category: category || null,
          modality,
          academy_name: getLocationName(location) || null,
          description: description || null,
          duration: duration,
          is_class: true,
        };

        // Check if recurrence is enabled
        if (
          recurrenceConfig.enabled &&
          recurrenceConfig.frequency !== 'none' &&
          recurrenceConfig.endDate
        ) {
          // Validate recurrence config
          const validation = validateRecurrenceConfig(
            recurrenceConfig,
            baseStartTime,
            3
          );
          if (!validation.isValid) {
            toast({
              title: 'Validation Error',
              description: validation.error || 'Invalid recurrence configuration',
              variant: 'destructive',
              duration: 3000,
            });
            setIsLoading(false);
            return;
          }

          // Generate recurring dates
          // Parse endDate from YYYY-MM-DD string and set to end of day in local timezone
          const endDate = parseEndDate(recurrenceConfig.endDate);
          
          const recurringDates = generateRecurringDates({
            startDate: baseStartTime,
            frequency: recurrenceConfig.frequency,
            daysOfWeek: recurrenceConfig.daysOfWeek,
            endDate,
            duration,
          });

          if (recurringDates.length === 0) {
            toast({
              title: 'Error',
              description: 'No dates generated for recurrence. Please check your configuration.',
              variant: 'destructive',
              duration: 3000,
            });
            setIsLoading(false);
            return;
          }

          // Filter out dates that have already passed (for today, only include if time hasn't passed)
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const filteredDates = recurringDates.filter(date => {
            const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            // If it's today, check if the time has passed
            if (dateOnly.getTime() === today.getTime()) {
              return date > now; // Only include if the time hasn't passed
            }
            // For future dates, always include
            return date > now;
          });

          if (filteredDates.length === 0) {
            toast({
              title: 'Error',
              description: 'No valid dates found. All selected dates and times have already passed.',
              variant: 'destructive',
              duration: 3000,
            });
            setIsLoading(false);
            return;
          }

          // Create multiple classes in batch for better performance
          const classesToInsert = filteredDates.map(date => ({
            ...baseClassData,
            start_time: date.toISOString(),
            end_time: new Date(
              date.getTime() + duration * 60000
            ).toISOString(),
            status: 'Scheduled',
          }));

          const { data: createdClassesData, error: batchError } = await supabase
            .from('trainings')
            .insert(classesToInsert)
            .select();

          let successCount = 0;
          let errorCount = 0;
          const createdClasses: any[] = [];

          if (batchError) {
            errorCount = filteredDates.length;
            setIsLoading(false);
            throw batchError; // Throw to be caught by catch block
          }
          
          if (!createdClassesData || createdClassesData.length === 0) {
            setIsLoading(false);
            throw new Error('Batch insert returned no data. No classes were created.');
          }
          
          createdClasses.push(...createdClassesData);
          successCount = createdClassesData.length;

          // Add students to all classes if it's a Private Class (batch insert)
          if (classType === 'Private Class' && students.length > 0 && createdClassesData.length > 0) {
            const allStudentInserts: any[] = [];
            
            for (const classData of createdClassesData) {
              const studentInserts = students.map(student => {
                if (student.username === 'manual') {
                  return {
                    training_id: classData.id,
                    manual_partner_name: student.full_name
                  };
                } else {
                  return {
                    training_id: classData.id,
                    partner_user_id: student.id
                  };
                }
              });
              allStudentInserts.push(...studentInserts);
            }

            if (allStudentInserts.length > 0) {
              const { error: studentsError } = await supabase
                .from('training_partners')
                .insert(allStudentInserts);

              if (studentsError) {
                console.error('Error adding students to recurring classes:', studentsError);
              }
            }
          }

          // Show feedback
          if (successCount > 0) {
            toast({
              title: t('calendar.created_recurring_trainings', {
                count: successCount,
              }),
              description: `Successfully created ${successCount} class${successCount > 1 ? 'es' : ''}`,
              duration: 3000,
            });
          }
          if (errorCount > 0) {
            toast({
              title: t('calendar.failed_to_create_trainings', {
                count: errorCount,
              }),
              description: `Failed to create ${errorCount} class${errorCount > 1 ? 'es' : ''}`,
              variant: 'destructive',
              duration: 3000,
            });
          }

          // Use first created class for success screen
          if (createdClasses.length > 0) {
            classData = createdClasses[0];
            setCreatedClass(classData);
            setStep('success');
            
            // Invalidate queries to refresh the calendar (non-blocking)
            Promise.all([
              queryClient.invalidateQueries({ queryKey: ['instructor-highlights'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['journey-highlights'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['instructor-classes'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['trainings'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['classes-with-notes'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['next-trainings'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['monthly-totals'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['monthly-classes'], exact: false }),
              queryClient.invalidateQueries({ queryKey: ['monthly-trainings'], exact: false }),
            ]).catch(() => {});
            
            // Call onClassCreated immediately (don't wait for query invalidation)
            onClassCreated?.(classData);
          } else {
            // If no classes were created, throw an error to be caught by the catch block
            throw new Error('No classes were created. All attempts failed.');
          }
        } else {
          // Create single class (no recurrence)
          const { data, error: classError } = await supabase
            .from('trainings')
            .insert({
              ...baseClassData,
              start_time: utcStartTime.toISOString(),
              end_time: utcEndTime.toISOString(),
              status: 'Scheduled',
            })
            .select()
            .single();

          if (classError) {
            throw classError;
          }
          
          classData = data;

          // Add students if it's a Private Class and students are provided
          if (classType === 'Private Class' && students.length > 0) {
            const studentInserts = students.map(student => {
              if (student.username === 'manual') {
                // Manual student - save with manual_partner_name
                return {
                  training_id: classData.id,
                  manual_partner_name: student.full_name
                };
              } else {
                // Regular student - save with partner_user_id
                return {
                  training_id: classData.id,
                  partner_user_id: student.id
                };
              }
            });

            if (studentInserts.length > 0) {
              const { error: studentsError } = await supabase
                .from('training_partners')
                .insert(studentInserts);

              if (studentsError) {
                console.error('Error adding students:', studentsError);
              }
            }
          }
          
          // Invalidate queries to refresh the calendar (non-blocking)
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ['instructor-highlights'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['journey-highlights'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['instructor-classes'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['trainings'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['classes-with-notes'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['next-trainings'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['monthly-totals'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['monthly-classes'], exact: false }),
            queryClient.invalidateQueries({ queryKey: ['monthly-trainings'], exact: false }),
          ]).catch(err => console.error('Error invalidating queries:', err));
          
          if (mode === 'edit') {
            // In edit mode, close modal directly and call update callback
            onClassUpdated?.();
            onClose();
          } else {
            // In create mode, show success modal
            if (mode === 'create') {
              setCreatedClass(classData);
              setStep('success');
            }
            onClassCreated?.(classData);
          }
        }
      }

    } catch (error) {
      console.error('Error saving class:', error);
      toast({
        title: t('notifications.toasts.calendar_class_creation_failed'),
        description: mode === 'edit' ? t('notifications.toasts.calendar_class_update_failed_description') : t('notifications.toasts.calendar_class_creation_failed_description'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Only reset form fields if not in edit mode
      if (mode === 'create') {
        setClassType('Group Class');
        setCategory('');
        setModality('Gi');
        setLocation('');
        setDate('');
        setStartTime('');
        setDuration(60);
        setDescription('');
        setStudents([]);
        setSelectedStudent(null);
        // Reset recurrence config to default (none)
        setRecurrenceConfig({
          enabled: false,
          frequency: 'none',
        });
      }
      setStep('form');
      setCreatedClass(null);
      onClose();
    }
  };

  // Handle class type change - reset students when switching to Group Class
  const handleClassTypeChange = (type: 'Group Class' | 'Private Class') => {
    setClassType(type);
    if (type === 'Group Class') {
      // Clear students when switching to Group Class
      setStudents([]);
      setSelectedStudent(null);
    }
    // If switching to Private Class, students can be added but are not required
  };

  // Handle student selection - automatically add when selected
  const handleStudentChange = (student: Student | string | null) => {
    if (student && classType === 'Private Class') {
      if (typeof student === 'string') {
        // Manual student
        const newStudent: Student = {
          id: `manual-${Date.now()}`,
          full_name: student,
          username: 'manual'
        };
        // Check if student is already in the list
        const isAlreadyAdded = students.some(s => s.full_name === student);
        if (!isAlreadyAdded) {
          setStudents(prev => [...prev, newStudent]);
        }
      } else {
        // Check if student is already in the list
        const isAlreadyAdded = students.some(s => s.id === student.id);
        if (!isAlreadyAdded) {
          setStudents(prev => [...prev, student]);
        }
      }
      // Clear the field after adding
      setSelectedStudent(null);
    } else {
      setSelectedStudent(student);
    }
  };

  // Remove student from the list
  const handleRemoveStudent = (studentToRemove: Student) => {
    setStudents(prev => prev.filter(student => student.id !== studentToRemove.id));
  };

  // Success modal - Only show in create mode
  if (step === 'success' && createdClass && mode === 'create') {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl p-4">
          <div className="flex flex-col items-center justify-center text-center px-1 py-6">
            {/* Title */}
            <h3 className="text-xl font-semibold text-black mb-6 font-roboto">
              Class Added!
            </h3>
            
            {/* Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            
            {/* Content */}
            <div className="space-y-4 mb-8">
              <p className="text-gray-600">
                Your class of {format(new Date(createdClass.start_time), 'MMMM d, yyyy')} was successfully added!
              </p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="font-semibold text-black">
                  {createdClass.title}
                </div>
                <div className="text-gray-600">
                  {format(new Date(createdClass.start_time), 'MMMM d, yyyy')} at {format(new Date(createdClass.start_time), 'h:mm a')}
                </div>
                <div className="text-gray-500 text-xs font-medium">
                  {createdClass.modality}
                </div>
              </div>
            </div>

            {/* Button */}
            <Button
              variant="primary"
              size="lg"
              onClick={handleClose}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="bg-white border-gray-300 rounded-2xl w-[90vw] max-w-md max-h-[90vh] flex flex-col p-0 overflow-hidden"
        aria-describedby="add-class-description"
      >
        <DialogDescription id="add-class-description" className="sr-only">
          {mode === 'edit' ? 'Edit your class details' : 'Add a new class to your schedule'}
        </DialogDescription>
        <SossietyDialogHeader title={mode === 'edit' ? t('calendar.edit_class') : t('calendar.add_class')}>
          <SossietyDialogClose onClick={handleClose} />
        </SossietyDialogHeader>

        {/* Scrollable content area */}
        <form id="add-class-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          <div className="space-y-4">
          {/* Type of Class - Toggle */}
          <div>
            <Label className="text-black">{t('calendar.type_of_class')}*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                onClick={() => handleClassTypeChange('Group Class')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  classType === 'Group Class'
                    ? 'bg-sossiety-red text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {t('calendar.group_class')}
              </button>
              <button
                type="button"
                onClick={() => handleClassTypeChange('Private Class')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  classType === 'Private Class'
                    ? 'bg-sossiety-red text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {t('calendar.private_class')}
              </button>
            </div>
          </div>

          {/* Modality - Gi/No-Gi Toggle */}
          <div>
            <Label className="text-black">{t('calendar.modality')}*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  modality === 'Gi'
                    ? 'bg-sossiety-red text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setModality('Gi')}
              >
                Gi
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  modality === 'No-Gi'
                    ? 'bg-sossiety-red text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setModality('No-Gi')}
              >
                No-Gi
              </button>
            </div>
          </div>

          {/* Category */}
          <div>
            <Label className="text-black">{t('calendar.category_optional')}</Label>
            <div className="w-full border border-gray-300 rounded-md focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-400">
              <Select
                value={category}
                onValueChange={(value) => setCategory(value)}
              >
                <SelectTrigger className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent text-sm [&_svg]:text-gray-500">
                  <SelectValue placeholder={t('calendar.select_category')} />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
                  {CLASS_CATEGORIES.map((categoryOption) => {
                    const categoryKey = getTrainingCategoryTranslationKey(categoryOption);
                    return (
                      <SelectItem key={categoryOption} value={categoryOption} className="text-white text-sm">
                        {t(`calendar.training_categories.${categoryKey}`) || categoryOption}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location */}
          <div>
            <Label className="text-black">{t('calendar.location')}</Label>
            <AcademyAutocomplete
              value={location}
              onChange={setLocation}
              placeholder={t('calendar.search_academy_placeholder')}
              containerClassName="w-full"
              theme="light"
            />
          </div>

          {/* Students - Only show for Private Class */}
          {classType === 'Private Class' && (
            <div>
              <Label className="text-black">{t('calendar.students')}</Label>
              <div className="flex-1">
                <StudentAutocomplete
                  value={selectedStudent}
                  onChange={handleStudentChange}
                  placeholder={t('calendar.search_students_placeholder')}
                  theme="light"
                  containerClassName="w-full"
                />
              </div>
              
              {/* Selected Students List */}
              {students.length > 0 && (
                <div className="mt-3 space-y-2">
                  {students.map((student) => (
                    <StudentDisplay
                      key={student.id}
                      student={{
                        id: student.id,
                        name: student.full_name,
                        username: student.username,
                        profile_picture_url: student.profile_picture_url,
                        belt_rank: student.belt_rank
                      }}
                      showRemoveButton={true}
                      onRemove={() => handleRemoveStudent(student)}
                      size="md"
                      className="p-2 rounded-lg"
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Date and Time */}
          {/* Hide Date when recurrence is enabled, show Time with full width */}
          {recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none' ? (
            <div className="w-full">
              <Label htmlFor="class-start-time" className="text-black text-sm block mb-1">{t('calendar.start_time')}*</Label>
              <TimeInput
                id="class-start-time"
                value={startTime}
                onChange={setStartTime}
                listId="class-time-options"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 items-start">
              <div className="w-full">
                <Label htmlFor="class-date" className="text-black text-sm block mb-1">{t('calendar.date')}*</Label>
                <DateInput
                  id="class-date"
                  value={date}
                  onChange={setDate}
                />
              </div>
              <div className="w-full">
                <Label htmlFor="class-start-time" className="text-black text-sm block mb-1">{t('calendar.start_time')}*</Label>
                <TimeInput
                  id="class-start-time"
                  value={startTime}
                  onChange={setStartTime}
                  listId="class-time-options"
                />
              </div>
            </div>
          )}

          {/* Recurrence Selector */}
          {mode === 'create' && (
            <RecurrenceSelector
              value={recurrenceConfig}
              onChange={setRecurrenceConfig}
              maxMonths={3}
              startDate={(() => {
                try {
                  // Use the SAME logic as in handleSubmit for consistency
                  // When recurrence is enabled, use today's date as the base (same as in creation)
                  let dateToUse = date;
                  if (recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none') {
                    // For recurrence, use today's date as the base (the actual dates come from generateRecurringDates)
                    dateToUse = new Date().toISOString().split('T')[0];
                  }
                  
                  if (!dateToUse) {
                    // Fallback to today if no date is available
                    const today = new Date();
                    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
                  }
                  
                  const [year, month, day] = dateToUse.split('-').map(Number);
                  
                  // If startTime is available, use it; otherwise use midnight
                  if (startTime) {
                    const time24Hour = convertTimeTo24Hour(startTime);
                    const [hours, minutes] = time24Hour.split(':').map(Number);
                    return new Date(year, month - 1, day, hours, minutes, 0, 0);
                  }
                  
                  // Return date at midnight if no time is available
                  return new Date(year, month - 1, day, 0, 0, 0, 0);
                } catch {
                  // Fallback to today if any error occurs
                  const today = new Date();
                  return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
                }
              })()}
            />
          )}

          {/* Duration */}
          <div>
            <Label htmlFor="duration" className="text-black font-medium">{t('calendar.duration_minutes')}*</Label>
            <Input
              id="duration"
              type="number"
              value={duration || ''}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="text-black border-gray-300 bg-white mt-1"
              min="15"
              max="480"
              step="15"
              labelColor="#000"
              required
            />
            {timeWarning && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">{timeWarning}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Description - Removed as per specification */}
          </div>
        </form>

        {/* Fixed Action Buttons at bottom */}
        <div className="flex-shrink-0 pt-4 pb-6 px-6 border-t border-gray-200 bg-white">
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline-light"
              size="md"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              {t('common.close')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={(e) => {
                e.preventDefault();
                const form = document.getElementById('add-class-form') as HTMLFormElement;
                if (form) {
                  form.requestSubmit();
                } else {
                  handleSubmit(e as any);
                }
              }}
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading 
                ? (mode === 'edit' ? t('common.updating') : t('common.creating')) 
                : (mode === 'edit' ? t('calendar.update_class') : t('calendar.create'))
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
